/**
 * @fileoverview Keyboard shortcuts hook for power users (dispatchers).
 *
 * Global shortcuts:
 * - Cmd/Ctrl + K: Focus search input on current page
 * - Cmd/Ctrl + / : Toggle shortcuts help overlay
 *
 * Only active in the business dashboard portal.
 */

import { useEffect, useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

export interface ShortcutDef {
  key: string;
  meta: boolean;
  label: string;
  description: string;
  action: () => void;
}

export function useKeyboardShortcuts() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showHelp, setShowHelp] = useState(false);

  // Only activate in business dashboard
  const isInDashboard = location.pathname.startsWith("/app/dashboard");

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isInDashboard) return;

      // Ignore if user is typing in an input/textarea
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.isContentEditable
      ) {
        // Allow Escape to blur inputs
        if (event.key === "Escape") {
          target.blur();
        }
        return;
      }

      const isMeta = event.metaKey || event.ctrlKey;

      // Cmd/Ctrl + K: Focus search input
      if (isMeta && event.key === "k") {
        event.preventDefault();
        const searchInput = document.querySelector<HTMLInputElement>(
          'input[placeholder*="Search"], input[placeholder*="search"]'
        );
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
        return;
      }

      // Cmd/Ctrl + / : Toggle shortcuts help
      if (isMeta && event.key === "/") {
        event.preventDefault();
        setShowHelp((prev) => !prev);
        return;
      }

      // Quick navigation (without meta key)
      switch (event.key) {
        case "g":
          // Wait for second key press (vim-style go-to)
          break;
        case "?":
          setShowHelp((prev) => !prev);
          break;
      }
    },
    [isInDashboard]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Close help on navigation
  useEffect(() => {
    setShowHelp(false);
  }, [location.pathname]);

  return {
    showHelp,
    setShowHelp,
    shortcuts: [
      { keys: "⌘ K", label: "Focus search" },
      { keys: "⌘ /", label: "Toggle shortcuts" },
      { keys: "?", label: "Show shortcuts" },
      { keys: "Esc", label: "Clear focus" },
    ] as const,
  };
}
